<template>
  <!-- Staff shell: full role-adaptive tabbed workspace. Staff drives the
       explicit condition; the customer shell is the fail-closed fallback. -->
  <template v-if="isStaffUser">
    <PageHeader
      :title="pageTitle"
      subtitle="Manage requirements and support tickets"
    >
      <template #bottom>
        <Page_Tabs
          :tabs="tabOptions"
          class="px-6 pt-4"
          no-border
        />
      </template>
    </PageHeader>

    <div :class="isFullBleed ? '' : 'p-6'">
      <div :class="isFullBleed ? '' : 'bg-white rounded border border-gray-200'">
        <Support_ServiceDesk
          v-if="activeTab === 'service-desk'"
          :selected-id="selectedId"
          :selected-title="selectedTicketTitle"
          @select="onSelect"
        />
        <Support_Activity v-else-if="activeTab === 'activity'" />
        <Support_Hades v-else-if="activeTab === 'hades'" />
        <Support_AppLatency v-else-if="activeTab === 'latency'" />
        <Support_Library
          v-else-if="activeTab === 'library'"
          :library-path="libraryPath"
          :library-tab="libraryTab"
          @title-change="setLibraryTitle"
        />
        <Support_List
          v-else
          :selected-id="selectedId"
          @select="onSelect"
        />
      </div>
    </div>
  </template>

  <!-- Customer shell: bare Service Desk, no internal chrome (absorbs the retired
       Support_Standalone). Fail-closed default for any non-staff user. -->
  <div v-else class="h-screen flex flex-col overflow-hidden">
    <Support_ServiceDesk
      :selected-id="selectedId"
      :selected-title="selectedTicketTitle"
      @select="onSelect"
    />
  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import Support_List from './Support_List.vue';
import Support_ServiceDesk from './Support_ServiceDesk.vue';
import Support_Activity from './Support_Activity.vue';
import Support_Hades from './Hades/Support_Hades.vue';
import Support_AppLatency from './Latency/Support_AppLatency.vue';
import Support_Library from './Library/Support_Library.vue';
import PageHeader from '@/components/widgetcomponents/library/PageHeader.vue';
import Page_Tabs from '@/components/widgetcomponents/Page_Tabs.vue';
import { COMPANY } from '@/constants';

// Requirements + Tickets merged into Work; Timeline is gone -- it was
// /api/requirements/ filtered to DONE_STATUSES ordered by -completed_at, which
// the merged list reproduces as Status = Done sorted by completed date. Activity
// survives because it reads /api/requirements/activity/, which returns events.
const TABS = [
  { label: 'Work', icon: ['fas', 'clipboard-list'], to: '/support/work' },
  { label: 'Activity', icon: ['fas', 'wave-square'], to: '/support/activity' },
  { label: 'Hades', icon: ['fas', 'magnifying-glass-chart'], to: '/support/hades' },
  { label: 'App Latency', icon: ['fas', 'gauge-high'], to: '/support/latency', superuserOnly: true },
  { label: 'Library', icon: ['fas', 'book-open'], to: '/support/library' },
  { label: 'Service Desk', icon: ['fas', 'headset'], to: '/support/service-desk' },
];

export default {
  name: 'Support_Overview',
  components: {
    Support_List,
    Support_ServiceDesk,
    Support_Activity,
    Support_Hades,
    Support_AppLatency,
    Support_Library,
    PageHeader,
    Page_Tabs,
  },
  props: {
    selectedId: { type: [String, Number], default: null },
    libraryPath: { type: String, default: null },
    libraryTab: { type: String, default: null },
  },
  data() {
    return {
      selectedTicketTitle: '',
      titleRequestId: 0,
      libraryTitle: '',
    };
  },
  computed: {
    ...mapGetters('auth', ['current_user']),
    isStaffUser() {
      // Kairon staff. Same predicate as Support_ServiceDesk / Support_ItemDetail
      // so the staff/customer split is consistent everywhere.
      return !!(this.current_user?.is_staff || this.current_user?.is_superuser);
    },
    isCustomerShell() {
      return !this.isStaffUser;
    },
    tabOptions() {
      return TABS.filter((tab) => !tab.superuserOnly || this.current_user?.is_superuser);
    },
    isFullBleed() {
      // Service Desk and Library manage their own header/toolbar and bleed to the edges.
      return this.activeTab === 'service-desk' || this.activeTab === 'library';
    },
    activeTab() {
      const path = this.$route.path;
      if (path.includes('service-desk')) return 'service-desk';
      if (path.includes('activity')) return 'activity';
      if (path.includes('hades')) return 'hades';
      if (path.includes('latency')) return 'latency';
      if (path.includes('library')) return 'library';
      return 'work';
    },
    pageTitle() {
      const section = this.activeTab === 'service-desk' ? 'Service Desk' : 'Support';
      if (this.selectedId == null) return section;
      const title = this.selectedTicketTitle.trim();
      return title
        ? `${section} #${this.selectedId} — ${title}`
        : `${section} #${this.selectedId}`;
    },
    browserTitle() {
      if (this.activeTab === 'work' || this.activeTab === 'service-desk') {
        const section = this.activeTab === 'service-desk' ? 'Service Desk' : 'Work';
        if (this.selectedId == null) return `${section} | ${COMPANY.NAME}`;
        const title = this.selectedTicketTitle.trim();
        const ticket = title ? `#${this.selectedId} - ${title}` : `#${this.selectedId}`;
        return `${ticket} | ${section} | ${COMPANY.NAME}`;
      }
      if (this.activeTab !== 'library') return `${this.pageTitle} | ${COMPANY.NAME}`;

      if (!this.libraryPath || !this.libraryTitle) return `Library | ${COMPANY.NAME}`;
      const tabLabels = {
        overview: 'Overview',
        checks: 'Checks',
        history: 'History',
        code: 'Code',
        related: 'Related',
      };
      const tabTitle = tabLabels[this.libraryTab];
      const title = tabTitle ? `${this.libraryTitle} — ${tabTitle}` : this.libraryTitle;
      return `${title} | Library | ${COMPANY.NAME}`;
    },
  },
  watch: {
    $route() {
      this.ensureCustomerRoute();
      this.setBrowserTitle();
    },
    selectedId: 'loadSelectedTicketTitle',
    browserTitle: 'setBrowserTitle',
  },
  created() {
    this.ensureCustomerRoute();
    this.loadSelectedTicketTitle();
  },
  mounted() {
    this.setBrowserTitle();
  },
  methods: {
    setLibraryTitle(title) {
      this.libraryTitle = typeof title === 'string' ? title : '';
    },
    setBrowserTitle() {
      document.title = this.browserTitle;
    },
    async loadSelectedTicketTitle() {
      const id = this.selectedId;
      const requestId = ++this.titleRequestId;
      this.selectedTicketTitle = '';
      if (id == null) return;

      try {
        const { data } = await axios.get(`/api/requirements/${id}/`);
        if (requestId === this.titleRequestId && String(this.selectedId) === String(id)) {
          this.selectedTicketTitle = typeof data?.title === 'string' ? data.title : '';
        }
      } catch {
        // The route still identifies the ticket even if its summary cannot load.
      }
    },
    ensureCustomerRoute() {
      // A customer may hold MODULE_SERVICE_DESK (so the route guard admits them
      // to any /support/* path) but must only ever see the Service Desk. Keep
      // them there.
      if (this.isCustomerShell && this.activeTab !== 'service-desk') {
        this.$router.replace({ path: '/support/service-desk', query: this.$route.query });
      }
    },
    onSelect(id) {
      const bases = {
        'service-desk': '/support/service-desk',
        work: '/support/work',
      };
      const base = bases[this.activeTab] || bases.work;
      this.$router.push({
        path: id ? `${base}/${id}` : base,
        query: this.$route.query,
      });
    },
  },
};
</script>
